import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Avatar,
  AnchoredPopover,
  AnchoredPopoverContent,
  AnchoredPopoverTrigger,
  DatePicker,
  formatPickerDate,
} from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import { Calendar, ChevronLeft, ChevronRight, ChevronDown, Building2, Home, Video, MapPin } from 'lucide-react';
import {
  TimeField,
  DateInput,
  DateSegment,
  type TimeValue,
} from 'react-aria-components';
import { Time } from '@internationalized/date';
import { useAvailableSlots } from '@/services/scheduling/use-available-slots';
import { useOrgLocations } from '@/scheduling/services/use-org-locations';
import { NO_SLOT_AVAILABILITY_MESSAGE } from '@/scheduling/lib/appointment-strings';
import { usePreBookSelectedSlot } from '@/services/scheduling/use-prebook-selected-slot';
import {
  pickDefaultReminder,
  pickAttachedBillingProfile,
  reminderRules,
  formatBillingProfile,
} from '@/services/scheduling/use-prebook-appointment-profile';
import { useAuthStore } from '@/auth/auth-store';
import { formatLocalDatetimeForWire } from '@/shared/lib/date-format';
import { roundUpToFiveMinutes } from '@/scheduling/lib/round-up-to-five-minutes';
import type { AppointmentFormValues } from '@/types/appointment-form';
import { practiceNow } from '@/shared/lib/practice-time';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

interface TimeDateSelectionViewProps {
  /** Closes the view without changes. */
  onCancel: () => void;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** Trigger + DS footer share this exact format so the two never drift. */
const DATE_FORMAT = 'MMMM dd, yyyy';

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function getStripDays(anchor: Date) {
  return Array.from({ length: 5 }, (_, i) => {
    const d = startOfDay(anchor);
    d.setDate(anchor.getDate() + i);
    return d;
  });
}

function isSameDay(a: Date, b: Date) {
  return a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
}

function fmt12h(iso: string) {
  const d = new Date(iso);
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'p' : 'a';
  return `${h > 12 ? h - 12 : h || 12}:${m}${ampm}`;
}

function getInitials(name: string) {
  const parts = name.replace(/^Dr\.?\s*/i, '').split(' ').filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0]).join('').toUpperCase();
}

function locationIcon(name: string) {
  switch (name) {
    case 'Office':
      return Building2;
    case 'Home':
      return Home;
    case 'Virtual':
      return Video;
    default:
      return MapPin;
  }
}

interface SlotItem {
  id: string;
  start: string;
  end: string;
}

/**
 * TimeDateSelectionView — full panel-content replacement.
 * Mirrors Flutter `time_date_selection_widget.dart`. Pushed when the user
 * taps the forward arrow on a provider row in `ProviderSelectionView`.
 *
 * Sections:
 *   - PROVIDER (read-only)
 *   - SELECTED SLOT (current pick + "Custom" header link — TODO)
 *   - SELECT LOCATION (chip row — pre-selected location stays leftmost)
 *   - Date picker (tappable header + 5-day strip)
 *   - Time slots grouped MORNING (00–12) / AFTERNOON (12–17) / EVENING (17–24)
 *
 * Save button is enabled only when a slot is picked AND it differs from
 * the appointment's current `start` (Flutter line 247).
 */
export function TimeDateSelectionView({ onCancel }: TimeDateSelectionViewProps) {
  const { watch, setValue, getValues } = useFormContext<AppointmentFormValues>();
  const providerName = watch('providerName');
  const providerSpeciality = watch('providerSpeciality');
  const selectedDate = watch('selectedDate');
  const appointmentProfileId = watch('appointmentProfileId');
  const minutesDuration = watch('minutesDuration');
  const selectedLocation = watch('location');
  const previousStart = watch('slotStart');

  const orgId = useAuthStore((s) => s.user?.organizationId) ?? '';
  const practiceId = useAuthStore((s) => s.user?.practiceId) ?? '';
  const userPractitioner = getValues('providerId');
  const provider = useMemo(
    () => ({ id: userPractitioner ?? '', name: providerName ?? '' }),
    [userPractitioner, providerName],
  );

  // Local state for in-flight picks (not yet committed to form state).
  const today = startOfDay(practiceNow());
  const initialDate = selectedDate ? startOfDay(new Date(selectedDate)) : today;
  const [pickerDate, setPickerDate] = useState<Date>(initialDate);
  // Anchor at today so the strip layout matches the panel's date picker;
  // the effect below shifts the anchor only if the pick falls outside the
  // current 5-day window.
  const [anchorDate, setAnchorDate] = useState<Date>(today);
  const [selectedSlot, setSelectedSlot] = useState<SlotItem | null>(null);

  // Keep the local pickerDate aligned with the form's selectedDate so a
  // date change made on the panel before opening this view is reflected
  // in the slot query and the strip highlight.
  useEffect(() => {
    if (!selectedDate) return;
    const fd = startOfDay(new Date(selectedDate));
    setPickerDate((prev) => (isSameDay(prev, fd) ? prev : fd));
  }, [selectedDate]);

  useEffect(() => {
    setAnchorDate((prev) => {
      const prevEnd = new Date(prev);
      prevEnd.setDate(prev.getDate() + 4);
      const startsBefore = pickerDate.getTime() < prev.getTime();
      const endsAfter = pickerDate.getTime() > prevEnd.getTime();
      return startsBefore || endsAfter ? pickerDate : prev;
    });
  }, [pickerDate]);

  // Org locations for the chip row. Keep the selected one leftmost
  // (Flutter parity: `time_date_selection_widget.dart:158–166`).
  const { data: locations = [] } = useOrgLocations(orgId);
  const orderedLocations = useMemo(() => {
    if (!selectedLocation || locations.length === 0) return locations;
    const idx = locations.findIndex((l) => l.id === selectedLocation.id);
    if (idx <= 0) return locations;
    const copy = [...locations];
    const [picked] = copy.splice(idx, 1);
    copy.unshift(picked);
    return copy;
  }, [locations, selectedLocation]);

  // Today is the floor — past dates aren't bookable. Flutter ships
  // `firstDate = DateTime.now().subtract(Duration(days: 1))` as a
  // buffer, but we tighten to today (matches user requirement).
  const minDate = startOfDay(practiceNow());

  const stripDays = getStripDays(anchorDate);

  const { data: apiSlots = [], isLoading } = useAvailableSlots(
    provider.id && practiceId && appointmentProfileId
      ? {
          practiceId,
          practitionerId: provider.id,
          appointmentProfileId,
          start: pickerDate.toISOString(),
          end: new Date(new Date(pickerDate).setHours(23, 59, 59)).toISOString(),
          count: 100,
        }
      : null,
  );

  // Always include the start in the id — backend has been observed to ship
  // `00000000-0000-0000-0000-000000000000` as the slot id, which collides
  // across the strip and trips React's duplicate-key warning.
  // Also drop slots whose start time is in the past — when the user
  // picks today, anything earlier than `now` isn't bookable.
  const nowMs = practiceNow().getTime();
  const slots: SlotItem[] = (apiSlots ?? [])
    .map((s, i) => ({
      id: `${s.start ?? ''}-${i}`,
      start: s.start ?? '',
      end: s.end ?? '',
    }))
    .filter((s) => s.start && s.end && Date.parse(s.start) >= nowMs);

  // Group by hour — MORNING [0,12), AFTERNOON [12,17), EVENING [17,24).
  const morningSlots = slots.filter((s) => {
    const h = new Date(s.start).getHours();
    return h >= 0 && h < 12;
  });
  const afternoonSlots = slots.filter((s) => {
    const h = new Date(s.start).getHours();
    return h >= 12 && h < 17;
  });
  const eveningSlots = slots.filter((s) => {
    const h = new Date(s.start).getHours();
    return h >= 17 && h < 24;
  });

  const shiftStrip = (deltaDays: number) => {
    const d = new Date(anchorDate);
    d.setDate(d.getDate() + deltaDays);
    if (d.getTime() < minDate.getTime()) return;
    setAnchorDate(d);
  };

  const saveEnabled = !!selectedSlot && selectedSlot.start !== previousStart;

  // PreBook the slot on save — refreshes reminder / billing / RFV for
  // the new context (Flutter `calendar_screen.dart:963–971`).
  const preBookSlot = usePreBookSelectedSlot();

  const onSave = () => {
    if (!selectedSlot) return;
    setValue('slotId', selectedSlot.id, { shouldValidate: true });
    setValue('slotStart', selectedSlot.start);
    setValue('slotEnd', selectedSlot.end);
    setValue('selectedDate', selectedSlot.start);

    if (provider.id && appointmentProfileId) {
      preBookSlot.mutate(
        {
          organizationId: orgId,
          practiceId,
          providerId: provider.id,
          appointmentProfileId,
        },
        {
          onSuccess: (response) => {
            const reminder = pickDefaultReminder(response.reminderList);
            if (reminder?.id) {
              setValue('reminderProfileId', reminder.id, { shouldValidate: true });
              setValue('reminderProfileName', reminder.profileName ?? null);
              setValue('reminderProfileRules', reminderRules(reminder));
            }
            // Re-fire on slot pick mirrors Flutter
            // (create_appointment_widget.dart:2240/2258) — keeps the
            // billing profile that's attached to the appointment profile.
            // `appointmentProfilesList` isn't on the typed AppointmentFormValues
            // (the field is populated by a side-effect on profile pick but
            // never declared on the schema). Cast the watch invocation.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const appointmentProfilesList = (watch as any)('appointmentProfilesList') as
              | Array<{ id?: string | null; billingProfileId?: string }>
              | undefined;
            const profileForBilling = appointmentProfilesList?.find(
              (p) => p.id === appointmentProfileId,
            );
            // pickAttachedBillingProfile reads `billingProfileId` off the
            // first arg; the appointment-profile row carries that field
            // but its full shape doesn't line up structurally with the
            // helper's minimal signature.
            const billing = pickAttachedBillingProfile(
              profileForBilling as { billingProfileId?: string } | undefined,
              response.billingProfiles,
            );
            if (billing?.id) {
              setValue('billingProfileId', billing.id);
              setValue('billingProfileName', formatBillingProfile(billing));
            }
          },
        },
      );
    }

    onCancel();
  };

  const slotChipClass = (s: SlotItem) =>
    cn(
      'h-10 px-3 rounded-full text-sm font-medium border transition-colors flex-shrink-0',
      selectedSlot?.id === s.id
        ? 'bg-brand-primary text-white border-brand-primary'
        : 'border-border-default bg-grey-100 text-text-primary hover:border-brand-primary hover:bg-blue-100',
    );

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="booking-subhead" data-slot="booking-subhead">
        <button
          onClick={onCancel}
          className="text-sm font-medium text-brand-primary hover:text-interactive-hover"
        >
          Cancel
        </button>
        <h2 className="booking-subhead-title">Select date and time</h2>
        <button
          type="button"
          onClick={onSave}
          disabled={!saveEnabled}
          className={cn(
            'text-sm font-medium',
            saveEnabled
              ? 'text-brand-primary hover:text-interactive-hover'
              : 'text-text-hint cursor-not-allowed',
          )}
        >
          Save
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide">
        <div className="px-4 py-4 space-y-6">
          {/* PROVIDER (read-only) */}
          <div>
            <p className="text-xs font-medium text-text-muted mb-2">
              Provider
            </p>
            <div className="flex items-center gap-3 p-3 rounded-lg border border-border-default bg-surface-card">
              <Avatar
                size="sm"
                className="flex-shrink-0"
                name={provider.name}
                initials={getInitials(provider.name)}
              />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-text-primary truncate">
                  {provider.name.replace(/^Dr\.?\s*/i, 'Dr. ')}
                </p>
                {providerSpeciality ? (
                  <p className="text-xs text-text-muted truncate">{providerSpeciality}</p>
                ) : null}
              </div>
            </div>
          </div>

          {/* SELECTED SLOT */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-text-muted">
                Selected Slot
              </p>
              <CustomTimePopover
                pickerDate={pickerDate}
                durationMinutes={minutesDuration ?? 30}
                initial={selectedSlot?.start ?? null}
                onConfirm={(slot) => setSelectedSlot(slot)}
              />
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg border border-border-default bg-surface-card">
              <Calendar className="size-5 text-text-secondary flex-shrink-0" />
              <div className="text-sm text-text-primary">
                {selectedSlot
                  ? `${MONTH_SHORT[new Date(selectedSlot.start).getMonth()]} ${String(
                      new Date(selectedSlot.start).getDate(),
                    ).padStart(2, '0')}, ${new Date(selectedSlot.start).getFullYear()} · ${fmt12h(selectedSlot.start)}`
                  : '— : —'}
              </div>
            </div>
          </div>

          {/* SELECT LOCATION — share row width with flex-1 when <=3 so the
              default Office/Virtual/Home set fits without a horizontal scroll;
              fall back to fixed-width + scroll for 4+ locations. */}
          <div>
            <p className="text-xs font-medium text-text-muted mb-2">
              Select Location
            </p>
            <div
              className={cn(
                'flex gap-2',
                orderedLocations.length <= 3 ? '' : 'overflow-x-auto scrollbar-hide pb-1',
              )}
            >
              {orderedLocations.map((loc) => {
                const Glyph = locationIcon(loc.name);
                const isSelected = selectedLocation?.id === loc.id;
                const fitsWithoutScroll = orderedLocations.length <= 3;
                return (
                  <button
                    key={loc.id}
                    type="button"
                    onClick={() =>
                      setValue('location', { id: loc.id, name: loc.name }, { shouldValidate: true })
                    }
                    className={cn(
                      'flex items-center gap-2 h-13 px-3 rounded-lg border transition-colors',
                      fitsWithoutScroll
                        ? 'flex-1 min-w-0'
                        : 'flex-shrink-0 min-w-30',
                      isSelected
                        ? 'bg-blue-100 border-brand-primary text-brand-primary'
                        : 'bg-surface-card border-border-default text-text-muted hover:border-border-strong',
                    )}
                  >
                    <Glyph className="size-4 flex-shrink-0" />
                    <span className="text-sm font-semibold truncate">{loc.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date picker */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <button type="button" onClick={() => shiftStrip(-5)} className="icon-action" aria-label="Previous days">
                <ChevronLeft className="size-4" />
              </button>
              <DatePicker
                value={pickerDate}
                onChange={(d) => {
                  if (d) {
                    setPickerDate(startOfDay(d));
                    setSelectedSlot(null);
                  }
                }}
                minDate={minDate}
                dateFormat={DATE_FORMAT}
                trigger={
                  <button
                    type="button"
                    className="sched-month-btn"
                  >
                    <span>{formatPickerDate(pickerDate, DATE_FORMAT)}</span>
                    <ChevronDown className="size-4" />
                  </button>
                }
              />
              <button type="button" onClick={() => shiftStrip(5)} className="icon-action" aria-label="Next days">
                <ChevronRight className="size-4" />
              </button>
            </div>
            <div className="grid grid-cols-5 gap-1">
              {stripDays.map((d) => {
                const isSelected = isSameDay(d, pickerDate);
                const isPast = d.getTime() < minDate.getTime();
                return (
                  <button
                    key={d.toISOString()}
                    type="button"
                    disabled={isPast}
                    onClick={() => {
                      setPickerDate(d);
                      setSelectedSlot(null);
                    }}
                    className="sched-day"
                    data-selected={isSelected || undefined}
                    data-off={isPast || undefined}
                  >
                    <span className="sched-day-meta">{MONTH_SHORT[d.getMonth()]}</span>
                    <span className="sched-day-num">{d.getDate()}</span>
                    <span className="sched-day-meta">{DAY_NAMES[d.getDay()]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time slots */}
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="h-16 rounded-lg bg-grey-200 animate-pulse" />
              ))}
            </div>
          ) : slots.length === 0 ? (
            <p className="text-sm text-text-muted py-2 leading-snug">
              {NO_SLOT_AVAILABILITY_MESSAGE}
            </p>
          ) : (
            <div className="space-y-4">
              {morningSlots.length > 0 && (
                <SlotGroup label="Morning" slots={morningSlots} chipClass={slotChipClass} onPick={setSelectedSlot} />
              )}
              {afternoonSlots.length > 0 && (
                <SlotGroup label="Afternoon" slots={afternoonSlots} chipClass={slotChipClass} onPick={setSelectedSlot} />
              )}
              {eveningSlots.length > 0 && (
                <SlotGroup label="Evening" slots={eveningSlots} chipClass={slotChipClass} onPick={setSelectedSlot} />
              )}
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

interface SlotGroupProps {
  label: string;
  slots: SlotItem[];
  chipClass: (s: SlotItem) => string;
  onPick: (s: SlotItem) => void;
}

function SlotGroup({ label, slots, chipClass, onPick }: SlotGroupProps) {
  return (
    <div>
      <p className="text-xs font-medium text-text-muted mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">
        {slots.map((s) => (
          <button key={s.id} type="button" onClick={() => onPick(s)} className={chipClass(s)}>
            {fmt12h(s.start)}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Seed the custom-time field. When re-opening on an existing pick (`initial`),
 * keep that exact time. On a fresh open (no pick) seed from *now*, rounded UP to
 * the next 5-minute division — Flutter parity: its picker snaps `TimeOfDay.now()`
 * to 5-minute steps, so 12:18 opens at 12:20 (already-aligned times stay put; a
 * round-up past :55 rolls into the next hour).
 */
function seedTime(initial: string | null): TimeValue {
  if (initial) {
    const d = new Date(initial);
    return new Time(d.getHours(), d.getMinutes());
  }
  const { hour, minute } = roundUpToFiveMinutes(practiceNow());
  return new Time(hour, minute);
}

interface CustomTimePopoverProps {
  pickerDate: Date;
  durationMinutes: number;
  initial: string | null;
  onConfirm: (slot: SlotItem) => void;
}

/**
 * CustomTimePopover — popover-based custom time entry.
 * Trigger: "Custom" link in the SELECTED SLOT header.
 * Body: a `react-aria-components` `TimeField` with three keyboard-navigable
 * segments (hour / minute / AM-PM) — type a digit or use ↑↓ to adjust.
 * "Set" builds a synthetic SlotItem from `pickerDate + chosen time`.
 */
function CustomTimePopover({ pickerDate, durationMinutes, initial, onConfirm }: CustomTimePopoverProps) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<TimeValue | null>(() => seedTime(initial));

  // Re-seed every time the popover opens so it reflects the latest pick.
  useEffect(() => {
    if (open) setValue(seedTime(initial));
  }, [open, initial]);

  const apply = () => {
    if (!value) return;
    const start = new Date(pickerDate);
    start.setHours(value.hour, value.minute, 0, 0);
    const end = new Date(start);
    end.setMinutes(start.getMinutes() + durationMinutes);
    // Emit LOCAL-CLOCK, no-Z strings (`YYYY-MM-DDTHH:mm:ss.sss`) — the same
    // shape the backend slot API returns and the book payload ships verbatim
    // (`transform-form-to-request.ts:312`). The backend applies the
    // practice/user timezone itself, so the wire value must be the literal
    // entered time. `Date.toISOString()` (UTC + `Z`) shifted the custom time
    // by the user's offset (e.g. 10:00 in Dubai → `06:00Z`), which the backend
    // then re-converted to the wrong slot — the reported "system books a
    // different time" bug. See `shared/lib/date-format.ts`.
    const startWire = formatLocalDatetimeForWire(start);
    const endWire = formatLocalDatetimeForWire(end);
    onConfirm({
      id: `custom-${startWire}`,
      start: startWire,
      end: endWire,
    });
    setOpen(false);
  };

  return (
    <AnchoredPopover open={open} onOpenChange={setOpen}>
      <AnchoredPopoverTrigger
        render={
          <button
            type="button"
            className="text-sm font-medium text-brand-primary hover:text-interactive-hover"
          >
            Custom
          </button>
        }
      />
      <AnchoredPopoverContent collisionAvoidance={{ fallbackAxisSide: 'end' }} align="end" className="w-auto p-3 gap-3">
        <p className="text-xs font-medium text-text-muted mb-2">
          Enter time
        </p>
        <TimeField value={value} onChange={setValue} hourCycle={12} aria-label="Custom time">
          <DateInput className="flex items-center h-10 px-3 rounded-lg border border-border-default bg-surface-card focus-within:border-brand-primary text-base text-text-primary tabular-nums">
            {(segment) => (
              <DateSegment
                segment={segment}
                className={cn(
                  'px-1 outline-none rounded',
                  'data-focused:bg-blue-100 data-focused:text-brand-primary',
                  'data-placeholder:text-text-hint',
                  'segment-literal:px-0',
                )}
              />
            )}
          </DateInput>
        </TimeField>
        <div className="flex justify-end gap-3 mt-3">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-sm font-medium text-text-muted hover:text-text-primary px-2 py-1"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={!value}
            className={cn(
              'text-sm font-medium px-3 py-1 rounded-md',
              value ? 'bg-brand-primary text-white hover:bg-interactive-hover' : 'bg-grey-200 text-text-hint cursor-not-allowed',
            )}
          >
            Set
          </button>
        </div>
      </AnchoredPopoverContent>
    </AnchoredPopover>
  );
}
